import { useState, useCallback } from "react";

/**
 * @typedef {"quest" | "festive"} PreviewType
 */

/**
 * @typedef {Object} PreviewItem
 * @property {number} id
 * @property {string} title
 * @property {string} description
 * @property {"jwplayer" | string} trailerType
 * @property {string | null} trailerHinJwp
 * @property {string | null} trailerEngJwp
 * @property {PreviewType} type
 * @property {string} created_at
 * @property {boolean} has_free_preview
 * @property {number} free_preview_count
 * @property {string} thumbnail_url
 * @property {Array<number>} nextChapterId
 */

/**
 * @typedef {Object} PreviewResponse
 * @property {boolean} success
 * @property {PreviewItem[]} data
 * @property {string} [message]
 */

/**
 * Fetch Preview Quests / Festive Content (No Auth Required)
 *
 * @returns {{
 *   fetchPreview: (type: PreviewType) => Promise<PreviewItem[]>,
 *   loading: boolean,
 *   error: string | null,
 *   data: PreviewItem[]
 * }}
 */
export const usePreviewQuests = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  const fetchPreview = useCallback(async (type) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiUrl}/student/quests/preview?type=${type}`,
        {
          method: "GET",
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to fetch preview data");
      }

      // If your API doesn't wrap in { success, data }, adjust here
      const finalData = Array.isArray(result) ? result : result.data;

      setData(finalData || []);
      return finalData || [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  return {
    fetchPreview,
    loading,
    error,
    data,
  };
};